import { AxiosResponse } from 'axios';
import { Buffer } from 'buffer';
import {
  ICreateAutomationRequest,
  ICreateAutomationResponse,
  IDeleteAutomationsReqestFilters,
  IDeleteAutomationsResponse,
  IDisableAutomationResponse,
  IDispatchAutomationResponse,
  IEnableAutomationResponse,
  IGetAutomationActionSettingsResponse,
  IGetAutomationActionsResponse,
  IGetAutomationByIdResponse,
  IGetAutomationFilter,
  IGetAutomationsResponse,
  IGetAutomationTriggerResponse,
  IGetAutomationTriggerSettingsResponse,
  IUpdateAutomationRequest,
  IUpdateAutomationResponse,
} from 'models/http/automations';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from 'utils/http';

class AutomationsHttp {
  private static route = '/automations';

  public async get(filters: IGetAutomationFilter, abortController?: AbortController): Promise<IGetAutomationsResponse['records']> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`${AutomationsHttp.route}?${queryParams}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetAutomationsResponse>) => {
        return response.data.records;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetAutomationByIdResponse> {
    return serverAxios
      .get(`${AutomationsHttp.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetAutomationByIdResponse>) => {
        return response.data;
      });
  }

  public async getTriggers(abortController?: AbortController): Promise<IGetAutomationTriggerResponse> {
    return serverAxios
      .get(`${AutomationsHttp.route}/triggers`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetAutomationTriggerResponse>) => {
        return response.data;
      });
  }

  public async getTriggerSettings(triggerType: string, abortController?: AbortController): Promise<IGetAutomationTriggerSettingsResponse> {
    return serverAxios
      .get(`${AutomationsHttp.route}/triggers/${triggerType}/settings`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetAutomationTriggerSettingsResponse>) => {
        return response.data;
      });
  }

  public async getActions(
    triggerType: string,
    triggerOptions: Record<string, unknown>,
    abortController?: AbortController | null,
  ): Promise<IGetAutomationActionsResponse> {
    const triggerOptionsJson = JSON.stringify(triggerOptions);
    const base64 = Buffer.from(triggerOptionsJson).toString('base64');

    return serverAxios
      .get(`${AutomationsHttp.route}/triggers/${triggerType}/actions?triggerOptions=${base64}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetAutomationActionsResponse>) => {
        return response.data;
      });
  }

  public async getActionSettings(
    triggerType: string,
    actionType: string,
    triggerOptions: Record<string, unknown>,
    abortController?: AbortController,
  ): Promise<IGetAutomationActionSettingsResponse> {
    const triggerOptionsJson = JSON.stringify(triggerOptions);
    const base64 = Buffer.from(triggerOptionsJson).toString('base64');

    return serverAxios
      .get(`${AutomationsHttp.route}/triggers/${triggerType}/actions/${actionType}/settings?triggerOptions=${base64}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetAutomationActionSettingsResponse>) => {
        return response.data;
      });
  }

  public async create(data: ICreateAutomationRequest): Promise<ICreateAutomationResponse> {
    return serverAxios
      .post(AutomationsHttp.route, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<ICreateAutomationResponse>) => {
        return response.data;
      });
  }

  public async update(automationId: string, data: IUpdateAutomationRequest): Promise<IUpdateAutomationResponse> {
    return serverAxios
      .patch(`${AutomationsHttp.route}/${automationId}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateAutomationResponse>) => {
        return response.data;
      });
  }

  public async enableAutomation(automationId: number): Promise<IEnableAutomationResponse> {
    return serverAxios
      .patch(
        `${AutomationsHttp.route}/${automationId}/enable`,
        {},
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IEnableAutomationResponse>) => response.data);
  }

  public async disableAutomation(automationId: number): Promise<IDisableAutomationResponse> {
    return serverAxios
      .patch(
        `${AutomationsHttp.route}/${automationId}/disable`,
        {},
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IDisableAutomationResponse>) => response.data);
  }

  public async dispatchAutomation(automationId: string): Promise<IDispatchAutomationResponse> {
    return serverAxios
      .post(
        `${AutomationsHttp.route}/${automationId}/dispatch`,
        {},
        {
          timeout: HttpTimeoutPriority.Medium,
        },
      )
      .then((response: AxiosResponse<IDispatchAutomationResponse>) => response.data);
  }

  public async delete(filters: number[] | IDeleteAutomationsReqestFilters): Promise<IDeleteAutomationsResponse> {
    return serverAxios
      .delete(AutomationsHttp.route, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteAutomationsResponse>) => {
        return response.data;
      });
  }
}

export default AutomationsHttp;
